import type { Editor } from 'slate';

import React, { useLayoutEffect, useState, useCallback, useEffect, memo, useReducer } from 'react';
import { Node } from 'slate';
import { useSlateStatic, ReactEditor } from 'slate-react';

import useEventListener from '@core/hooks/useEventListener';
import useUniqueId from '@core/hooks/useUniqueId';

import Flex from '@ui/Flex';
import Input from '@ui/Input';
import { Code, CodeTabs } from '@ui/MarkdownEditor/editor/blocks';
import { MenuActionTypes } from '@ui/MarkdownEditor/enums';
import type { CodeTabsElement } from '@ui/MarkdownEditor/types';
import Menu, { MenuDivider } from '@ui/Menu';
import MenuItem from '@ui/Menu/Item';
import Select from '@ui/Select';

import MenuDropdown from '../MenuDropdown';

import classes from './style.module.scss';
import useCodeSettings from './useCodeSettings';

const hasParent = (node: HTMLElement, fn: (el: HTMLElement) => boolean): boolean => {
  return !!(fn(node) || (node.parentElement && hasParent(node.parentElement, fn)));
};

const isCodeTabsTabClick = (editor: Editor, event: React.MouseEvent) => {
  try {
    return (
      CodeTabs.is(ReactEditor.toSlateNode(editor, event.target as HTMLElement)) &&
      hasParent(event.target as HTMLElement, (el: HTMLElement) => el.dataset.component === CodeTabs.tabId)
    );
  } catch {
    return false;
  }
};

const getLabel = (lang: string): string => {
  return Code.languages[lang] || lang;
};

const CodeSettings = () => {
  const uid = useUniqueId('CodeSettings');
  const editor = useSlateStatic();
  const [{ active, path, open }, dispatch] = useCodeSettings();

  const node: CodeTabsElement | null = path ? (Node.get(editor, path) as CodeTabsElement) : null;
  const [target, setTarget] = useState<HTMLButtonElement | null>(null);
  const customTabName = node?.tabs?.[node?.active]?.meta || '';
  const displayTabName = customTabName || getLabel(node?.tabs?.[node?.active]?.lang || 'text');
  const [, forceRender] = useReducer(x => x + 1, 0);

  useLayoutEffect(() => {
    if (!node) return;
    /*
     * Ideally, this would be more declarative, but since the code tabs aren't
     * actually elements in the editor state, we don't get the same
     * assurances and conveniences to look up their dom nodes from
     * `ReactEditor`. We'd instead have to manage the current active refs, and
     * I'm just really tired right now.
     */
    const tab = ReactEditor.toDOMNode(editor, node).querySelector(`[data-index="${active}"]`);

    setTarget(tab as HTMLButtonElement);
  }, [active, editor, node]);

  useEffect(() => {
    if (!node) setTarget(null);
  }, [node]);

  const close = useCallback(() => {
    dispatch({ type: MenuActionTypes.close });
  }, [dispatch]);

  useEventListener('keydown', (event: KeyboardEvent) => open && event.key === 'Escape' && close());

  const onChangeInput = useCallback(
    event => {
      if (!node) return;

      const { value } = event.target;

      CodeTabs.operations.updateCode(editor, { meta: value }, { at: path, index: active });
    },
    [active, editor, node, path],
  );

  const onKeyDownInput = useCallback(
    event => {
      if (event.key === 'Enter') close();
    },
    [close],
  );

  const onDeleteTab = useCallback(() => {
    if (!node) return;

    CodeTabs.operations.removeCodeTab(editor, path, active);

    close();
    ReactEditor.focus(editor);
  }, [active, close, editor, node, path]);

  const onChangeLanguage = useCallback(
    event => {
      if (!node) return;

      // set new lang
      CodeTabs.operations.setLang(editor, event.target.value, { at: path, index: active });

      // @todo: Restructure this menu so we don't have to force a re-render
      forceRender();
    },
    [node, editor, path, active],
  );

  /*
   * We want to pass control of the actual open state to the `CodeTabs`
   * component if we're clicking on a `Tabs` component. The `CodeTabs`
   * components has it's own logic about the state of the menu in regards to
   * the active tab. :shrug:
   */
  const onMenuClose = useCallback(
    event => {
      if (!isCodeTabsTabClick(editor, event)) {
        close();
      }
    },
    [close, editor],
  );

  /* Using a unique key forces a re-render when we change tabs. The input component doesn't reset its value if the new value is an empty string */
  const key = `${path ? [...path, node?.active].join(',') : ''}`;

  const options = Object.entries(Code.languages).map(([lang, label]) => {
    const option = {
      label,
      selected: lang === node?.tabs?.[node?.active]?.lang,
      value: lang,
    };
    return option;
  });

  return (
    <MenuDropdown close={onMenuClose} open={open} target={{ current: target }}>
      <Menu className={classes.CodeSettings}>
        <MenuItem focusable={false}>
          <Flex align="stretch" gap="xs" layout="col">
            <label className={classes['CodeSettings-Label']} htmlFor={uid('language')}>
              Syntax Highlighting
            </label>
            <Select
              id={uid('language')}
              onChange={e => onChangeLanguage(e)}
              options={options}
              size="sm"
              value={node?.tabs?.[node?.active]?.lang || ''}
            />
          </Flex>
        </MenuItem>

        <MenuItem focusable={false}>
          <Flex gap="xs" layout="col">
            <label className={classes['CodeSettings-Label']} htmlFor={uid('name')}>
              Tab Name
            </label>
            <Input
              key={key}
              className={classes['CodeSettings-Input']}
              id={uid('name')}
              onChange={onChangeInput}
              onKeyDown={onKeyDownInput}
              size="sm"
              value={displayTabName}
            />
          </Flex>
        </MenuItem>

        <MenuDivider />

        <MenuItem className={classes['CodeSettings-Menu']} color="red" onClick={onDeleteTab}>
          Delete Tab
        </MenuItem>
      </Menu>
    </MenuDropdown>
  );
};

export default memo(CodeSettings);

export { default as useCodeSettings } from './useCodeSettings';
